import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getShippingEstimateStringWithVerb,
  getShippingTitleString,
} from 'typescript/utilities/shipping_strings';
import { getCurrencyString } from 'typescript/utilities/currency';
import {
  AddressValidationResult,
  ValidationResponseAddress,
} from 'typescript/types/api/address_validation';
import AddressValidation from 'typescript/apps/checkout_v3/components/address_validation/address_validation';
import EmptyStateWithLoading from 'typescript/components/empty_state_with_loading';
import { useGetOrderSummary } from 'typescript/hooks/use_order_actions_client';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import {
  useGetShippingRates,
  useSelectShippingRate,
} from 'typescript/hooks/use_shipping_rates_client';
import { hasShipAddress } from 'typescript/utilities/order_data';
import {
  useInvalidateQuery,
  useIsProcessingAnyRequest,
} from 'typescript/hooks/use_react_query_utils';
import { QUERY_KEYS } from 'typescript/constants/react_query';
import { isValidationSuccess } from 'typescript/utilities/address_validation';
import {
  COMPONENT_NAMES,
  CHECKOUT_PAGE_CLICK_EVENTS,
} from 'typescript/constants/tracking';
import OptionRowsWithLogging from 'typescript/apps/checkout_v3/components/option_rows_with_logging';
import { onCheckoutPageClick } from 'typescript/utilities/tracking';
import { useToaster } from 'typescript/hooks/use_toaster';

export const SHIPPING_RATES_TEST_ID = 'shipping-rates-test-id';

interface ShippingRatesAndAddressValidationProps {
  showEmptyState?: boolean;
  showPoboxBlocked?: boolean;
  addressValidationResult?: AddressValidationResult;
  updateAddress?: (validationAddress: ValidationResponseAddress) => void;
}

/**
 * 1. if no address validation and no shipping rates, show the empty state
 *      a. if loading either address validation or shipping rates, show the loading spinner
 * 2. if partially verified or failed verification, show the address validation component
 * 3. otherwise, show the shipping rates component
 */
const ShippingRatesAndAddressValidation = ({
  showEmptyState = false,
  showPoboxBlocked = false,
  addressValidationResult,
  updateAddress,
}: ShippingRatesAndAddressValidationProps) => {
  const { t } = useTranslation();
  const { invalidate } = useInvalidateQuery();
  const { isProcessing } = useIsProcessingAnyRequest();
  const { orderNumber, trackingData } = useCheckoutFlow();
  const { orderSummaryData } = useGetOrderSummary({ orderNumber });
  const {
    shippingRates,
    isFetching: isGetShippingRatesFetching, // fetching => show loading for post initial fetch
    isPending: isGetShippingRatesPending, // pending => show loading for initial fetch
  } = useGetShippingRates({
    orderNumber,
    shouldFetch: hasShipAddress(orderSummaryData?.shipping_address),
  });
  const { selectShippingRate } = useSelectShippingRate({
    orderNumber,
  });
  const { showToast } = useToaster();

  const showAddressValidation =
    addressValidationResult && !isValidationSuccess(addressValidationResult);

  const onAddressValidationCTA = (option: 'original' | 'corrected') => {
    onCheckoutPageClick({
      eventDesc:
        option === 'original'
          ? CHECKOUT_PAGE_CLICK_EVENTS.address_validation_original
          : CHECKOUT_PAGE_CLICK_EVENTS.address_validation_corrected,
      trackingData,
    });

    updateAddress(addressValidationResult[`${option}_address`]);
  };

  const selectRate = (shippingRateCode: string) => {
    selectShippingRate(
      { shippingRateCode },
      {
        onSuccess: () => {
          invalidate(QUERY_KEYS.PAYMENT_OPTIONS, { orderNumber });
        },
        onError: () => {
          showToast({
            message: t('checkout.delivery.toast.failedShipRateSelect'),
            variant: 'critical',
          });
        },
      },
    );
  };

  const onShippingRateSelect = ({ optionId }) => {
    onCheckoutPageClick({
      eventDesc: CHECKOUT_PAGE_CLICK_EVENTS.shipping_rates_select,
      trackingData,
    });
    selectRate(optionId);
  };

  const showEmptyStateOrLoading =
    showEmptyState ||
    showPoboxBlocked ||
    ((isGetShippingRatesFetching || isGetShippingRatesPending) &&
      !showAddressValidation);

  // Adding a pickup-eligible product reserves the nearest store for pickup, so an
  // order can reach Ship to me still set to pickup: no speed is selected and Continue
  // does nothing. Pick the cheapest speed, as orders that can't be picked up start
  // with. Only once, so a failed attempt leaves the choice to the Seller.
  const hasSelectedDefaultRate = useRef(false);
  useEffect(() => {
    if (
      hasSelectedDefaultRate.current ||
      !orderSummaryData?.bopis_fulfillment_selected ||
      showEmptyStateOrLoading ||
      showAddressValidation ||
      isProcessing ||
      !shippingRates?.length ||
      shippingRates.some((rate) => rate.selected)
    ) {
      return;
    }

    hasSelectedDefaultRate.current = true;
    const cheapestRate = shippingRates.reduce((cheapest, rate) =>
      rate.cost < cheapest.cost ? rate : cheapest,
    );
    selectRate(cheapestRate.code);
  }, [
    orderSummaryData?.bopis_fulfillment_selected,
    showEmptyStateOrLoading,
    showAddressValidation,
    isProcessing,
    shippingRates,
  ]);

  if (showEmptyStateOrLoading) {
    return (
      <EmptyStateWithLoading
        className="shop-grid-item-full"
        // we'll opt to use isProcessing here to show the loading spinner
        // for any request that is run while the above conditions are true
        showLoading={isProcessing}
        description={
          showPoboxBlocked
            ? t('checkout.delivery.shipping.poboxBlocked')
            : t('checkout.delivery.shipping.addressMissing')
        }
      />
    );
  }

  // local development note: to test different address validation responses locally
  // you can go to the address_validation_response.rb file and uncomment the different responses
  if (showAddressValidation) {
    return (
      <AddressValidation
        className="shop-grid-item-full"
        addressValidationResult={addressValidationResult}
        onOriginalClick={() => onAddressValidationCTA('original')}
        onCorrectedClick={() => onAddressValidationCTA('corrected')}
      />
    );
  }

  return (
    <OptionRowsWithLogging
      dataTestId={SHIPPING_RATES_TEST_ID}
      className="shop-grid-item-full"
      options={shippingRates?.map((rate) => ({
        id: rate.code,
        title: getShippingTitleString(rate.code),
        subtext: [getShippingEstimateStringWithVerb(rate.range)],
        sideLabel: getCurrencyString({
          amount: rate.cost,
          currency: rate.currency,
        }),
        value: rate.code,
        selected: rate.selected,
      }))}
      onOptionSelect={onShippingRateSelect}
      componentName={COMPONENT_NAMES.APPS.CHECKOUT_V3.SHIPPING_RATES}
    />
  );
};

export default ShippingRatesAndAddressValidation;
